import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, X, FileDown } from 'lucide-react';
import { getApiUrl } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const API_URL = getApiUrl();

const STATUS_STYLE = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  auto_approved: 'bg-sky-50 text-sky-700 border-sky-200',
  downloaded: 'bg-slate-50 text-slate-600 border-slate-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200',
  instant: 'bg-violet-50 text-violet-700 border-violet-200',
};
const STATUS_LABEL = { auto_approved: 'Auto-approved', instant: 'Super Admin (instant)' };

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-');

const Filters = ({ filters }) => {
  const entries = Object.entries(filters || {});
  if (!entries.length) return <span className="text-slate-400">No filters</span>;
  return <span className="text-slate-500">{entries.map(([k, v]) => `${k}: ${v}`).join(' · ')}</span>;
};

export const DownloadRequestsPanel = () => {
  const { user } = useAuth();
  const canDecide = user?.role === 'super_admin';
  const [items, setItems] = useState([]);
  const [onlyPending, setOnlyPending] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/download-requests`, { withCredentials: true });
      setItems(data.requests || []);
    } catch { toast.error('Failed to load download requests'); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const decide = async (r, action) => {
    let reason = null;
    if (action === 'reject') {
      reason = window.prompt(`Reject ${r.user_name}'s "${r.label}" download? Optional reason:`, '');
      if (reason === null) return;
    }
    try {
      await axios.post(`${API_URL}/api/download-requests/${r.request_id}/${action}`, { reason }, { withCredentials: true });
      toast.success(action === 'approve' ? 'Download approved' : 'Download rejected');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  const shown = onlyPending ? items.filter((r) => r.status === 'pending') : items;
  const pendingCount = items.filter((r) => r.status === 'pending').length;

  return (
    <div className="card p-0 overflow-hidden" data-testid="download-requests-panel">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <FileDown className="w-4 h-4 text-indigo-600" />
          <h2 className="font-semibold text-slate-900">Download data requests</h2>
          {pendingCount > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid="download-requests-pending-count">{pendingCount} pending</span>}
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" checked={onlyPending} onChange={(e) => setOnlyPending(e.target.checked)} data-testid="download-requests-only-pending" />
          Pending only
        </label>
      </div>
      <p className="px-5 pt-3 text-xs text-slate-500">Exports by non-Super-Admin users are held for 30 minutes. If no action is taken they are auto-approved.</p>
      {shown.length === 0 ? (
        <p className="px-5 py-6 text-sm text-slate-400" data-testid="download-requests-empty">No download requests.</p>
      ) : (
        <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
          {shown.map((r) => (
            <div key={r.request_id} className="px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-3 text-sm" data-testid={`download-request-${r.request_id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900">{r.user_name} <span className="text-xs font-normal text-slate-400">· {r.user_role?.replace(/_/g, ' ')}</span></p>
                <p className="text-xs text-slate-700">{r.label}</p>
                <p className="text-xs truncate"><Filters filters={r.filters} /></p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Requested {fmt(r.created_at)}{r.status === 'pending' ? ` · auto-approves ${fmt(r.available_at)}` : ''}
                  {r.decided_by_name ? ` · ${r.status === 'rejected' ? 'rejected' : 'approved'} by ${r.decided_by_name}` : ''}
                  {r.reject_reason ? ` · "${r.reject_reason}"` : ''}
                </p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full border self-start sm:self-center ${STATUS_STYLE[r.status] || ''}`} data-testid={`download-request-status-${r.request_id}`}>
                {STATUS_LABEL[r.status] || r.status.charAt(0).toUpperCase() + r.status.slice(1)}
              </span>
              {canDecide && ['pending', 'auto_approved', 'approved'].includes(r.status) && (
                <div className="flex gap-2">
                  {r.status !== 'approved' && (
                    <button onClick={() => decide(r, 'approve')} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`download-request-approve-${r.request_id}`}><Check className="w-3 h-3" /> Approve</button>
                  )}
                  <button onClick={() => decide(r, 'reject')} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100" data-testid={`download-request-reject-${r.request_id}`}><X className="w-3 h-3" /> Reject</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DownloadRequestsPanel;
